import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DrivePageRow, ProjectDrive, UntaggedProjectRow } from '../../../shared/drives';
import { PHASE_LABELS } from '../../../shared/types';
import { api } from '../../lib/api';
import { formatProjectNumber } from '../../components/primitives';
import { backupDrivePayload } from './helpers';

export function AutomaticBackupChip() {
  return <span className="bb-chip" title="Linked automatically. Cannot be removed and does not replace a physical backup.">
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" aria-hidden="true"><rect x="3" y="7" width="10" height="7" rx="1" /><path d="M5 7V4a3 3 0 0 1 6 0v3" /></svg>Backblaze (auto)
  </span>;
}
export function ProjectPhase({ phase }: { phase: UntaggedProjectRow['phase'] }) {
  return <span className="ut-phase"><span className={`phase-dot ${phase}`} />{PHASE_LABELS[phase]}</span>;
}
export function UntaggedRow({ project, drives, onSaved }: {
  project: UntaggedProjectRow; drives: DrivePageRow[]; onSaved: () => void;
}) {
  const [backup, setBackup] = useState(project.backup?.drive_id ?? '');
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [noFootage, setNoFootage] = useState(false);
  const [automatic, setAutomatic] = useState(project.has_auto_backup);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retryFootage, setRetryFootage] = useState<boolean | null>(null);
  const writing = useRef(false);
  const number = formatProjectNumber(project.project_number);
  const change = (value: string) => {
    setBackup(value);
    setDirty(true); setSaved(false); setError(''); setRetryFootage(null);
  };
  const save = async (nextNoFootage = noFootage) => {
    if (writing.current) return;
    writing.current = true; setBusy(true); setError(''); setSaved(false);
    let driveWriteDone = false;
    try {
      const current = await api<{ drives: ProjectDrive[] }>(`/api/admin/projects/${project.project_id}/drives`);
      const payload = backupDrivePayload(current.drives, backup);
      await api(`/api/admin/projects/${project.project_id}/drives`, { method: 'PUT', json: payload });
      driveWriteDone = true;
      const result = await api<{ drives: ProjectDrive[] }>(`/api/admin/projects/${project.project_id}/footage`, { method: 'PATCH', json: { no_footage: nextNoFootage } });
      setNoFootage(nextNoFootage); setAutomatic(result.drives.some(link => !!link.auto_backup));
      setSaved(true); setDirty(false); setRetryFootage(null); onSaved();
    } catch (e) {
      setDirty(true);
      setRetryFootage(nextNoFootage);
      setError(`${driveWriteDone ? 'Drives saved, but footage status was not confirmed. Retry Save. ' : ''}${(e as Error).message}`);
    } finally { writing.current = false; setBusy(false); }
  };
  // The working copy's drive can't double as its backup; automatic drives never appear.
  const picker = <select
    className={`dpick${backup ? ' set' : ' ph'}`} aria-label={`Backup drive for ${project.name}`}
    value={backup} disabled={busy} onChange={e => change(e.target.value)}>
    <option value="">Add a physical backup</option>
    {drives.filter(drive => !drive.auto_backup && drive.id !== project.primary?.drive_id).map(drive =>
      <option value={drive.id} key={drive.id}>{drive.label}{drive.status === 'active' ? '' : ` (${drive.status})`}</option>)}
    {backup && !drives.some(drive => drive.id === backup) && <option value={backup}>{project.backup?.label ?? 'Unavailable drive'}</option>}
  </select>;
  return <div className={`ut-row${dirty ? ' dirty' : ''}${saved ? ' saved' : ''}${noFootage ? ' nofootage' : ''}`} data-project-id={project.project_id}>
    <div className="ut-pj"><Link to={`/projects/${project.project_id}`} className="no">{number ?? 'Project'}</Link><Link to={`/projects/${project.project_id}`} className="nm">{project.name}</Link></div>
    {noFootage ? <div className="ut-nf-line"><span>No footage · not asked again</span><button className="ut-restore" disabled={busy} onClick={() => void save(false)}>{busy ? 'Restoring…' : 'Restore'}</button></div> : <>
      <span className="ut-cl">{project.client_name}</span><ProjectPhase phase={project.phase} />
      <label className="ut-pick ut-backup"><span className="ut-mobile-label">Backup drive</span>{automatic && <AutomaticBackupChip />}{picker}</label>
      <label className="ut-nf"><span className="ut-toggle"><input type="checkbox" checked={false} disabled={busy} aria-label={`No footage for ${project.name}`} onChange={() => void save(true)} /><span className="toggle sm" aria-hidden="true" /></span><span>No footage</span></label>
      <span className="ut-save">{saved ? <span role="status">Saved ✓</span> : <button className={`btn${dirty ? ' primary' : ''}`} disabled={busy || !dirty} onClick={() => void save(retryFootage ?? false)}>{busy ? 'Saving…' : 'Save'}</button>}</span>
    </>}
    {error && <div className="ut-error" role="alert">{error}{noFootage && <button className="btn" disabled={busy} onClick={() => void save(false)}>Retry Restore</button>}</div>}
  </div>;
}
